import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articleAPI } from '../services/api';
import TrendingTopics from '../components/TrendingTopics';
import RewardToast from '../components/RewardToast';
import PinnedArticle from '../components/PinnedArticle';
import { useRewardTracking } from '../hooks/useRewardTracking';
import AdUnit from '../components/AdUnit';
import { CoinbaseBanner } from '../components/CoinbaseAffiliate';
import './HomePage.css';

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [breakingNews, setBreakingNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const { trackArticleRead, toast, setToast } = useRewardTracking();

  // 🎲 Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      // Mixed feed: Politics → AI → Crypto interleaved, shuffled within day
      const response = await articleAPI.getMixedFeed(0, 10);
      const allArticles = Array.isArray(response.data) ? response.data : [];

      // Breaking news pinned to top, rest stays in mixed order
      const breakingArticles = allArticles.filter(a => a.is_breaking);
      const regularArticles  = allArticles.filter(a => !a.is_breaking);
      const finalArticles = [...shuffleArray(breakingArticles), ...regularArticles];

      console.log(`✨ HomePage mixed feed: ${breakingArticles.length} breaking + ${regularArticles.length} regular = ${finalArticles.length} total`);

      setArticles(finalArticles);
      if (breakingArticles.length > 0) setBreakingNews(breakingArticles[0]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
      setLoading(false);
    }
  };

  const handleArticleClick = (articleId) => {
    trackArticleRead(articleId);
  };

  const getCategoryColor = (category) => {
    const colors = {
      crypto: '#f39c12',
      ai: '#3498db',
      politics: '#e74c3c'
    };
    return colors[category] || '#666';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const SentimentIcon = ({ sentiment, size = '16px' }) => {
    if (!sentiment) return null;

    const icons = {
      positive: '📈',
      negative: '📉',
      neutral: '➡️'
    };

    const height = size === '20px' ? '22px' : size === '16px' ? '20px' : size === '12px' ? '18px' : '20px';

    return (
      <span
        style={{
          fontSize: size,
          padding: '2px 6px',
          backgroundColor: '#f0f0f0',
          borderRadius: '3px',
          display: 'inline-flex',
          alignItems: 'center',
          height: height
        }}
        title={`Sentiment: ${sentiment}`}
      >
        {icons[sentiment]}
      </span>
    );
  };



  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading news...</p>
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <div className="no-articles">
        <h2>No articles found</h2>
        <p>Check back soon for the latest news!</p>
      </div>
    );
  }

  // Articles already prioritized: breaking first, then regular (both shuffled)
  const leadArticle = articles[0];
  const secondaryArticles = articles.slice(1, 5);
  const sidebarArticles = articles.slice(5, 10);

  return (
    <div className="homepage-cnn">
      {/* Reward Toast */}
      {toast && (
        <RewardToast
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {breakingNews && (
        <div className="breaking-banner">
          <div className="breaking-content">
            <span className="breaking-label">BREAKING</span>
            <Link
              to={`/article/${breakingNews.id}`}
              className="breaking-title"
              onClick={() => handleArticleClick(breakingNews.id)}
            >
              {breakingNews.title}
            </Link>
            <span className="breaking-time">{formatTimeAgo(breakingNews.published_at)}</span>
          </div>
        </div>
      )}

      <div className="homepage-container">
        {/* Ad space hidden - uncomment when adding Google AdSense */}
        <AdUnit slot="1111111111" style={{ minHeight: '90px', marginBottom: '16px' }} />

        <div className="content-grid">
          <main className="main-content">
            {leadArticle && (
              <article className="lead-article">
                <Link
                  to={`/article/${leadArticle.id}`}
                  onClick={() => handleArticleClick(leadArticle.id)}
                >
                  {leadArticle.image_url ? (
                    <img
                      src={leadArticle.image_url}
                      alt={leadArticle.title}
                      className="lead-image"
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '380px',
                      backgroundColor: getCategoryColor(leadArticle.category),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '36px',
                      fontWeight: 'bold',
                      opacity: 0.3,
                      marginBottom: '15px'
                    }}>
                      {leadArticle.category?.toUpperCase()}
                    </div>
                  )}
                </Link>
                <div className="lead-content">
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    marginBottom: '8px',
                    flexWrap: 'wrap',
                    minHeight: '24px'
                  }}>
                    {leadArticle.is_breaking && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: '#cc0000',
                        color: 'white',
                        padding: '4px 10px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        borderRadius: '3px',
                        height: '22px'
                      }}>
                        🔥 BREAKING
                      </span>
                    )}
                    <span
                      className="category-badge"
                      style={{
                        backgroundColor: getCategoryColor(leadArticle.category),
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: '22px',
                        padding: '4px 10px',
                        fontSize: '10px'
                      }}
                    >
                      {leadArticle.category?.toUpperCase()}
                    </span>
                    <SentimentIcon sentiment={leadArticle.sentiment} size="20px" />
                  </div>
                  <Link
                    to={`/article/${leadArticle.id}`}
                    onClick={() => handleArticleClick(leadArticle.id)}
                  >
                    <h1 className="lead-title">{leadArticle.title}</h1>
                  </Link>
                  <p className="lead-summary">
                    {leadArticle.ai_summary || leadArticle.summary}
                  </p>
                  <div className="article-meta">
                    <span className="article-source">{leadArticle.source_name && (leadArticle.source_name.toLowerCase().includes('focusview') ? leadArticle.source_name : `${leadArticle.source_name} via FocusView.ai`)}</span>
                    <span className="article-time">{formatTimeAgo(leadArticle.published_at)}</span>
                  </div>
                </div>
              </article>
            )}

            {/* Ad space hidden - uncomment when adding Google AdSense */}
            <AdUnit slot="2222222222" style={{ minHeight: '250px', marginBottom: '16px' }} />

            <div className="secondary-grid">
              {secondaryArticles.map(article => (
                <article key={article.id} className="secondary-article">
                  <Link
                    to={`/article/${article.id}`}
                    onClick={() => handleArticleClick(article.id)}
                  >
                    {article.image_url ? (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="secondary-image"
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '160px',
                        backgroundColor: getCategoryColor(article.category),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        opacity: 0.3,
                        marginBottom: '10px'
                      }}>
                        {article.category?.toUpperCase()}
                      </div>
                    )}
                  </Link>
                  <div className="secondary-content">
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'center',
                      marginBottom: '6px',
                      flexWrap: 'wrap',
                      minHeight: '22px'
                    }}>
                      {article.is_breaking && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: '#cc0000',
                          color: 'white',
                          padding: '3px 8px',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          borderRadius: '3px',
                          height: '20px'
                        }}>
                          🔥 BREAKING
                        </span>
                      )}
                      <span
                        className="category-badge-small"
                        style={{
                          backgroundColor: getCategoryColor(article.category),
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: '20px',
                          padding: '3px 8px',
                          fontSize: '9px'
                        }}
                      >
                        {article.category?.toUpperCase()}
                      </span>
                      <SentimentIcon sentiment={article.sentiment} size="16px" />
                    </div>
                    <Link
                      to={`/article/${article.id}`}
                      onClick={() => handleArticleClick(article.id)}
                    >
                      <h3 className="secondary-title">{article.title}</h3>
                    </Link>
                    <p className="secondary-summary">
                      {(article.ai_summary || article.summary || '').substring(0, 100)}...
                    </p>
                    <div className="article-meta">
                      <span className="article-source">{article.source_name && (article.source_name.toLowerCase().includes('focusview') ? article.source_name : `${article.source_name} via FocusView.ai`)}</span>
                      <span className="article-time">{formatTimeAgo(article.published_at)}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </main>

          <aside className="sidebar">
            {/* Admin Pinned Article — static top-right, survives refresh */}
            <PinnedArticle />

            {/* Ad space hidden - uncomment when adding Google AdSense */}
            <AdUnit slot="3333333333" style={{ minHeight: '250px', marginBottom: '16px' }} />

            <TrendingTopics />

            <div className="sidebar-articles">
              {sidebarArticles.map(article => (
                <article key={article.id} className="sidebar-article">
                  <Link
                    to={`/article/${article.id}`}
                    onClick={() => handleArticleClick(article.id)}
                  >
                    {article.image_url ? (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="sidebar-image"
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '120px',
                        backgroundColor: getCategoryColor(article.category),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: 0.3,
                        marginBottom: '8px'
                      }}>
                        {article.category?.toUpperCase()}
                      </div>
                    )}
                  </Link>
                  <div className="sidebar-content">
                    <div style={{
                      display: 'flex',
                      gap: '5px',
                      alignItems: 'center',
                      marginBottom: '5px',
                      flexWrap: 'wrap',
                      minHeight: '18px'
                    }}>
                      {article.is_breaking && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: '#cc0000',
                          color: 'white',
                          padding: '2px 6px',
                          fontSize: '8px',
                          fontWeight: 'bold',
                          borderRadius: '3px',
                          height: '18px'
                        }}>
                          🔥 BREAKING
                        </span>
                      )}
                      <span
                        className="category-badge-tiny"
                        style={{
                          backgroundColor: getCategoryColor(article.category),
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: '18px',
                          padding: '2px 6px',
                          fontSize: '8px'
                        }}
                      >
                        {article.category?.toUpperCase()}
                      </span>
                      <SentimentIcon sentiment={article.sentiment} size="12px" />
                    </div>
                    <Link
                      to={`/article/${article.id}`}
                      onClick={() => handleArticleClick(article.id)}
                    >
                      <h4 className="sidebar-title">{article.title}</h4>
                    </Link>
                    <div className="article-meta-small">
                      <span className="article-time">{formatTimeAgo(article.published_at)}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Ad space hidden - uncomment when adding Google AdSense */}
            <CoinbaseBanner style={{ marginBottom: '16px' }} />
          </aside>
        </div>

        {/* Ad space hidden - uncomment when adding Google AdSense */}
        <AdUnit slot="4444444444" style={{ minHeight: '90px', marginTop: '16px' }} />
      </div>
    </div>
  );
};

export default HomePage;